
import axiosInstance from "../axiosConfig";

// The axios interceptor rejects with the server body; mirror that shape.
const unsupported = (what) => {
  const message = `${what} is not supported by the server`;
  return { error: message, message };
};

// Service for handling inventory related API calls
const inventoryService = {
  // Get all inventory items
  getInventory: async () => {
    try {
      const response = await axiosInstance.get("/inventory");
      return response.data;
    } catch (error) {
      console.error("Error fetching inventory:", error);
      throw error;
    }
  },

  // Get inventory item by id
  getInventoryItemById: async (id) => {
    try {
      const response = await axiosInstance.get(`/products/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching inventory item with id ${id}:`, error);
      throw error;
    }
  },

  // Create new inventory item
  createInventoryItem: async (itemData) => {
    try {
      const response = await axiosInstance.post("/products", itemData);
      return response.data;
    } catch (error) {
      console.error("Error creating inventory item:", error);
      throw error;
    }
  },

  // Update inventory item
  updateInventoryItem: async (id, itemData) => {
    try {
      const response = await axiosInstance.put(`/products/${id}`, itemData);
      return response.data;
    } catch (error) {
      console.error(`Error updating inventory item with id ${id}:`, error);
      throw error;
    }
  },

  // Delete inventory item
  deleteInventoryItem: async (id) => {
    try {
      const response = await axiosInstance.delete(`/products/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting inventory item with id ${id}:`, error);
      throw error;
    }
  },

  // Get current stock levels
  getStockLevels: async () => {
    try {
      const response = await axiosInstance.get("/inventory/stock-levels");
      return response.data;
    } catch (error) {
      console.error("Error fetching stock levels:", error);
      throw error;
    }
  },

  // Record inventory adjustment
  recordAdjustment: async (adjustmentData) => {
    try {
      const response = await axiosInstance.post(
        "/inventory-adjustments",
        adjustmentData
      );
      return response.data;
    } catch (error) {
      console.error("Error recording inventory adjustment:", error);
      throw error;
    }
  },

  // Unified stock ledger (adjustments, transfers, receipts, returns, issues)
  getInventoryMovements: async (params = {}) => {
    try {
      const response = await axiosInstance.get("/inventory/movements", { params });
      return response.data?.data || [];
    } catch (error) {
      console.error("Error fetching inventory movements:", error);
      throw error;
    }
  },

  // Record inventory transfer between locations
  recordTransfer: async (transferData) => {
    try {
      const response = await axiosInstance.post(
        "/inventory-transfers",
        transferData
      );
      return response.data;
    } catch (error) {
      console.error("Error recording inventory transfer:", error);
      throw error;
    }
  },

  // Get inventory transfer by id
  getTransferById: async (id) => {
    try {
      const response = await axiosInstance.get(`/inventory-transfers/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching inventory transfer with id ${id}:`, error);
      throw error;
    }
  },

  // Get all inventory transfers
  getAllTransfers: async (params) => {
    try {
      const response = await axiosInstance.get("/inventory-transfers", {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching inventory transfers:", error);
      throw error;
    }
  },

  // Get inventory valuation
  getValuation: async (params) => {
    try {
      const response = await axiosInstance.get("/inventory-valuation", {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching inventory valuation:", error);
      throw error;
    }
  },

  // Get inventory locations
  getLocations: async () => {
    try {
      const response = await axiosInstance.get("/inventory/locations");
      return response.data;
    } catch (error) {
      console.error("Error fetching inventory locations:", error);
      throw error;
    }
  },

  // Create new inventory location
  // No backend endpoint exists for this; fail clearly.
  createLocation: async () => {
    throw unsupported("Creating inventory locations");
  },

  // Update inventory location
  // No backend endpoint exists for this; fail clearly.
  updateLocation: async () => {
    throw unsupported("Updating inventory locations");
  },

  // Get stock movement related to sales
  // No backend endpoint exists for this; fail clearly.
  getSalesStockImpact: async () => {
    throw unsupported("Sales stock impact");
  },

  // Get stock movement related to purchases
  // No backend endpoint exists for this; fail clearly.
  getPurchaseStockImpact: async () => {
    throw unsupported("Purchase stock impact");
  },

  // Get inventory summary with KPIs
  getInventorySummary: async () => {
    try {
      const response = await axiosInstance.get("/inventory/summary");
      return response.data;
    } catch (error) {
      console.error("Error fetching inventory summary:", error);
      throw error;
    }
  },

  // Get inventory batches and lot tracking
  getBatchesAndLots: async (productId) => {
    try {
      const response = await axiosInstance.get(
        `/inventory-valuation/lots/${productId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching batches for product ${productId}:`, error);
      throw error;
    }
  },

  // Get reorder suggestions based on min/max levels
  getReorderSuggestions: async () => {
    try {
      const response = await axiosInstance.get(
        "/inventory/reorder-suggestions"
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching reorder suggestions:", error);
      throw error;
    }
  },

  // Get products (alias for getInventory for compatibility)
  getProducts: async (params = {}) => {
    try {
      const response = await axiosInstance.get("/products", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching products:", error);
      throw error;
    }
  },
};

export default inventoryService;
